/**
 * selectionSize — turning "make this half-width" into a grid position.
 *
 * The precision path for resizing has been either a modifier chord or a
 * keyboard shortcut, both of which are recall-based and neither of which
 * exists on touch. Presets and numeric entry turn resizing from a POINTING
 * task (aim at a small handle, drag accurately) into a SELECTION task (press
 * a large, labelled button) — which is the whole point: large targets, no
 * aiming, no occlusion, identical on mouse and finger.
 *
 * Pure on purpose: the arithmetic is where the off-by-one lives, and it should
 * be testable without a store, a grid, or a render.
 */

export interface GridPosition {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface SizeConstraints {
  readonly minW?: number | undefined;
  readonly maxW?: number | undefined;
  readonly minH?: number | undefined;
  readonly maxH?: number | undefined;
}

/** Width presets, as a fraction of the grid's columns. */
export type WidthPreset = "third" | "half" | "twoThirds" | "full";

export const WIDTH_PRESETS: ReadonlyArray<{
  readonly id: WidthPreset;
  readonly label: string;
  readonly fraction: number;
}> = [
  { id: "third", label: "⅓", fraction: 1 / 3 },
  { id: "half", label: "½", fraction: 1 / 2 },
  { id: "twoThirds", label: "⅔", fraction: 2 / 3 },
  { id: "full", label: "Full", fraction: 1 },
];

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(Math.max(v, lo), hi);

/**
 * Columns for a preset. Rounded, then floored at 1: a ⅓ preset on a 4-column
 * grid is 1.33 columns, and silently producing 0 would delete the item's
 * width. Never exceeds the grid.
 */
export function presetColumns(preset: WidthPreset, columns: number): number {
  const cols = Math.max(1, Math.floor(columns));
  const found = WIDTH_PRESETS.find((p) => p.id === preset);
  if (!found) return cols;
  return clamp(Math.round(cols * found.fraction), 1, cols);
}

/**
 * Apply a width preset to a position.
 *
 * `x` is pulled back when the new width would overflow the grid — growing an
 * item that sits at the right edge should widen it leftwards rather than push
 * it out of bounds, which is what an author means by "make this full width".
 */
export function applyWidthPreset(
  position: GridPosition,
  preset: WidthPreset,
  columns: number,
  constraints: SizeConstraints = {},
): GridPosition {
  const cols = Math.max(1, Math.floor(columns));
  const wanted = presetColumns(preset, cols);
  const w = clamp(
    wanted,
    Math.max(1, constraints.minW ?? 1),
    Math.min(cols, constraints.maxW ?? cols),
  );
  return { ...position, w, x: clamp(position.x, 0, cols - w) };
}

/**
 * Apply an explicit width/height, ignoring blank or non-numeric input rather
 * than coercing it — a half-typed number in a field must not resize anything.
 */
export function applyExplicitSize(
  position: GridPosition,
  next: { readonly w?: number | undefined; readonly h?: number | undefined },
  columns: number,
  constraints: SizeConstraints = {},
): GridPosition {
  const cols = Math.max(1, Math.floor(columns));
  const w =
    typeof next.w === "number" && Number.isFinite(next.w)
      ? clamp(
          Math.round(next.w),
          Math.max(1, constraints.minW ?? 1),
          Math.min(cols, constraints.maxW ?? cols),
        )
      : position.w;
  const h =
    typeof next.h === "number" && Number.isFinite(next.h)
      ? clamp(
          Math.round(next.h),
          Math.max(1, constraints.minH ?? 1),
          constraints.maxH ?? Number.MAX_SAFE_INTEGER,
        )
      : position.h;
  return { ...position, w, h, x: clamp(position.x, 0, cols - w) };
}

/** Which preset (if any) the current width already matches — for showing the
 *  active state, so the control reflects reality instead of guessing. */
export function activeWidthPreset(
  position: GridPosition,
  columns: number,
): WidthPreset | null {
  for (const p of WIDTH_PRESETS)
    if (presetColumns(p.id, columns) === position.w) return p.id;
  return null;
}
